import Vue from 'vue';
import bookshelfService from '@d-services/bookshelf-service/bookshelf-service';
import profileService from '@d-services/profile-service/profile-service';
import AddToLibraryButton from '@d-shared/blog/add-to-library-button';
import { getErrorMessage } from '@d-shared/utils/errors';
import Window from '@d-shared/utils/window';
import { arrayToChunks } from '@d-shared/utils/array-to-chunks';

const BOOKS_PER_REQUEST = 25;

export default class BlogPostManager {
  constructor() {
    this.addToLibraryButtons = null;
    this.bookshelfOptions = null;
    this.bookshelfEntries = [];
    this.activeButton = null;

    this.clickEventPerformed = this.clickEventPerformed.bind(this);
    this.initBookshelfEntries = this.initBookshelfEntries.bind(this);
  }

  async install() {
    this.removeDraggableAttribute();

    const buyButtons = document.querySelectorAll('.buy-button[data-href]');
    buyButtons.forEach(this.initBuyButton);

    this.addToLibraryButtons = document.querySelectorAll('.add-to-library-button');
    if (!this.addToLibraryButtons.length) return;

    this.bookshelfOptions = await bookshelfService.getBookshelvesOptions();
    const isUserSignedIn = await this.isUserSignedIn();

    if (isUserSignedIn) {
      const bookSlugs = Array.from(this.addToLibraryButtons).map((node) => node.getAttribute('data-slug'));
      const bookSlugsChunks = arrayToChunks(bookSlugs, BOOKS_PER_REQUEST);
      await Promise.all(bookSlugsChunks.map(this.initBookshelfEntries));
    }

    this.addToLibraryButtons.forEach((node) => new AddToLibraryButton(node, this, this.bookshelfOptions, isUserSignedIn, this.bookshelfEntries));
  }

  async initBookshelfEntries(bookSlugs) {
    try {
      const firstPageData = await bookshelfService.getEntriesBySlug(bookSlugs);
      this.bookshelfEntries = this.bookshelfEntries.concat(firstPageData['bookshelf_entries']);

      if (firstPageData.meta.total_pages === 1) return;

      let currentPage = 2;

      while (currentPage <= firstPageData.meta.total_pages) {
        const responseData = await bookshelfService.getEntriesBySlug(bookSlugs, currentPage);
        this.bookshelfEntries = this.bookshelfEntries.concat(responseData['bookshelf_entries']);
        currentPage++;
      }
    } catch (error) {
      Vue.duiNotificationManager.error(getErrorMessage(error));
    }
  }

  // Need to do it because old posts can contain block with draggable=true
  removeDraggableAttribute() {
    const blots = document.querySelectorAll('.book-blot');

    Array.from(blots).forEach((blot) => {
      blot.draggable = false;
    });
  }

  initBuyButton(buyButton) {
    let bookSlug = buyButton.getAttribute('data-slug');

    // Need it for old blog posts, what doesn't have data-slug attribute in buy buttons
    if (!bookSlug) {
      const nextAddToLibraryButton = buyButton.parentNode.querySelector('.add-to-library-button');
      bookSlug = nextAddToLibraryButton.getAttribute('data-slug');
    }

    buyButton.addEventListener('click', () => {
      Vue.prototype.eventsTracking.startNewChain('buy-book', {
        bookSlug,
        location: 'blurb',
      });
      Window.open(buyButton.getAttribute('data-href'), '_blank');
    });
  }

  clickEventPerformed(e) {
    if (e.target.closest('.add-to-library-wrapper')) return;
    this.closeActiveButton();
  }

  setActiveButton(button) {
    this.activeButton = button;
    document.addEventListener('click', this.clickEventPerformed, true);
  }

  removeActiveButton() {
    this.activeButton = null;
    document.removeEventListener('click', this.clickEventPerformed, true);
  }

  closeActiveButton() {
    if (!this.activeButton) return;

    this.activeButton.closeBookshelvesContainer();
    document.removeEventListener('click', this.clickEventPerformed, true);
  }

  changeActiveButton(button) {
    this.closeActiveButton();
    this.setActiveButton(button);
  }

  async isUserSignedIn() {
    let profileResponse;

    try {
      profileResponse = await profileService.getProfile();
    } catch(error) {
      // We don't need to think about error handling
    }

    return profileResponse && profileResponse.slug;
  }
}
